"use client";

import { Info } from "lucide-react";

interface MarketplaceEquilibriumWidgetProps {
  className?: string;
  style?: React.CSSProperties;
}

// Format number in Spanish style (from the real codebase)
const formatNumber = (value: number) => {
  const absValue = Math.abs(value);
  return new Intl.NumberFormat("ca-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(absValue);
};

// Mock data with realistic values
const mockEquilibriumData = {
  marketBalance: 548320,
  availableGap: 423650,
  availableGapPercent: 42,
  utilizationRate: 24.5,
};

export function MarketplaceEquilibriumWidget({
  className,
  style,
}: MarketplaceEquilibriumWidgetProps) {
  const { marketBalance, availableGap, availableGapPercent, utilizationRate } = mockEquilibriumData;

  return (
    <div className={className} style={style}>
      {/* Responsive card that fits container */}
      <div className="bg-zinc-100 rounded-lg p-2 w-full flex flex-col">
        <h3 className="text-[8px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">
          Equilibrio del Marketplace
        </h3>
        <div className="bg-white rounded-lg shadow-sm overflow-hidden flex-1">
          <div className="grid grid-cols-3 divide-x divide-zinc-200 h-full">
            {/* Metric 1: Balance cupo vs. capacidad */}
            <div className="px-2 py-2 flex flex-col">
              <div className="flex items-center gap-1 mb-0.5">
                <p className="text-[8px] font-semibold text-zinc-500">Balance cupo vs. capacidad</p>
                <Info size={8} className="text-zinc-400 cursor-help" />
              </div>
              <div className="flex-1 flex items-center justify-center">
                <div>
                  <p className="text-xl font-bold text-zinc-900">
                    +{formatNumber(marketBalance)}
                    <span className="text-xs font-medium ml-0.5">€</span>
                  </p>
                  <p className="text-[8px] text-zinc-400 text-center">Exceso de cupo generado</p>
                </div>
              </div>
            </div>

            {/* Metric 2: Gap Disponible */}
            <div className="px-2 py-2 flex flex-col">
              <div className="flex items-center gap-1 mb-0.5">
                <p className="text-[8px] font-semibold text-zinc-500">Gap Disponible</p>
                <Info size={8} className="text-zinc-400 cursor-help" />
              </div>
              <div className="flex-1 flex items-center justify-center">
                <div>
                  <p className="text-xl font-bold text-zinc-900">
                    +{formatNumber(availableGap)}
                    <span className="text-xs font-medium ml-0.5">€</span>
                  </p>
                  <p className="text-[8px] text-zinc-400 text-center">
                    {availableGapPercent}% cobertura inversor
                  </p>
                </div>
              </div>
            </div>

            {/* Metric 3: Tasa de Utilizacion */}
            <div className="px-2 py-2 flex flex-col">
              <div className="flex items-center gap-1 mb-0.5">
                <p className="text-[8px] font-semibold text-zinc-500">Tasa de Utilizacion</p>
                <Info size={8} className="text-zinc-400 cursor-help" />
              </div>
              <div className="flex-1 flex items-center justify-center">
                <div>
                  <p className="text-xl font-bold text-zinc-900">
                    {utilizationRate.toFixed(1)}
                    <span className="text-xs font-medium ml-0.5">%</span>
                  </p>
                  <div className="h-1 bg-zinc-100 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full bg-zinc-900 rounded-full"
                      style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MarketplaceEquilibriumWidget;
